import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageBackground from '../components/PageBackgroundBeam';
import PageFooter from '../components/PageFooter';
import './PageOrigenInversion2.css';

function formatNumber(n) {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const COLORS = { 1: '#60a5fa', 2: '#4ade80', 3: '#fbbf24', 4: '#f87171' };
const LABELS = { 1: 'TV', 2: 'RADIO', 3: 'OOH', 4: 'PRINT' };

const ALL_YEARS = [
  { year: 2011, data: [
    { id: 1, value: 179768216 },
    { id: 2, value: 2727670 },
    { id: 3, value: 0 },
    { id: 4, value: 81051278 },
  ]},
  { year: 2014, data: [
    { id: 1, value: 298746210 },
    { id: 2, value: 14848977 },
    { id: 3, value: 0 },
    { id: 4, value: 92621566 },
  ]},
  { year: 2017, data: [
    { id: 1, value: 306571678 },
    { id: 2, value: 18592174 },
    { id: 3, value: 10277529 },
    { id: 4, value: 68030637 },
  ]},
  { year: 2020, data: [
    { id: 1, value: 218462123 },
    { id: 2, value: 32887725 },
    { id: 3, value: 5814378 },
    { id: 4, value: 25031635 },
  ]},
  { year: 2023, data: [
    { id: 1, value: 267262539 },
    { id: 2, value: 43936576 },
    { id: 3, value: 17226956 },
    { id: 4, value: 20316325 },
  ]},
  { year: 2025, data: [
    { id: 1, value: 353510190 },
    { id: 2, value: 35869784 },
    { id: 3, value: 28517929 },
    { id: 4, value: 16245978 },
  ]},
];

function PieChart({ data, year, size = 240 }) {
  const [hovered, setHovered] = useState(null);
  const filtered = data.filter((d) => d.value > 0);
  const total = data.reduce((s, d) => s + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 6;
  const innerRadius = radius * 0.55;

  let cumAngle = -Math.PI / 2;
  const slices = filtered.map((d) => {
    const pct = d.value / total;
    const angle = pct * 2 * Math.PI;
    const startAngle = cumAngle;
    const endAngle = cumAngle + angle;
    cumAngle = endAngle;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const ix1 = cx + innerRadius * Math.cos(endAngle);
    const iy1 = cy + innerRadius * Math.sin(endAngle);
    const ix2 = cx + innerRadius * Math.cos(startAngle);
    const iy2 = cy + innerRadius * Math.sin(startAngle);
    const largeArc = angle > Math.PI ? 1 : 0;

    const path = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      `L ${ix1} ${iy1}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix2} ${iy2}`,
      'Z',
    ].join(' ');

    const midAngle = startAngle + angle / 2;
    const labelR = (radius + innerRadius) / 2;
    const lx = cx + labelR * Math.cos(midAngle);
    const ly = cy + labelR * Math.sin(midAngle);

    return { ...d, pct, path, lx, ly, color: COLORS[d.id] };
  });

  return (
    <div className="pie-card">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="pie-svg">
        <defs>
          {slices.map((s) => (
            <filter key={s.id} id={`mm6-${year}-${s.id}`}>
              <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor={s.color} floodOpacity="0.35" />
            </filter>
          ))}
        </defs>
        {slices.map((s) => (
          <path
            key={s.id}
            d={s.path}
            fill={s.color}
            fillOpacity={hovered === s.id ? 1 : 0.85}
            stroke={hovered === s.id ? '#fff' : 'rgba(15,23,42,0.8)'}
            strokeWidth={hovered === s.id ? 2.5 : 2}
            filter={`url(#mm6-${year}-${s.id})`}
            className="pie-slice"
            onMouseEnter={() => setHovered(s.id)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
        {slices.map((s) => {
          const show = hovered === s.id;
          const labelR = radius + 18;
          const midAngle = Math.atan2(s.ly - cy, s.lx - cx);
          const tx = cx + labelR * Math.cos(midAngle);
          const ty = cy + labelR * Math.sin(midAngle);
          return show ? (
            <g key={`tip-${s.id}`} className="pie-tooltip-g" pointerEvents="none">
              <rect
                x={tx - 36} y={ty - 12}
                width="72" height="24"
                rx="6"
                fill="rgba(15,23,42,0.92)"
                stroke={s.color}
                strokeWidth="1"
              />
              <text x={tx} y={ty + 1} textAnchor="middle" dominantBaseline="middle" className="pie-tooltip-text" fill={s.color}>
                {LABELS[s.id]} {(s.pct * 100).toFixed(1)}%
              </text>
            </g>
          ) : null;
        })}
        <text x={cx} y={cy - 8} textAnchor="middle" dominantBaseline="middle" className="pie-center-year">
          {year}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" dominantBaseline="middle" className="pie-center-total">
          ${formatNumber(total)}
        </text>
      </svg>
    </div>
  );
}

function PageMixMedioInversion2() {
  const navigate = useNavigate();

  return (
    <PageBackground>
      <div className="origen2-page">
        <header className="origen2-header">
          <button className="btn-back" onClick={() => navigate('/mixmedio-inversion')}>
            &#8592; Mix de Medios
          </button>
          <div className="origen2-title-block">
            <h1 className="origen2-title">Mix de Medios &middot; Evoluci&oacute;n</h1>
            <p className="origen2-subtitle">Distribuci&oacute;n por tipo de medio &middot; Honduras 2011–2025 &middot; USD</p>
          </div>
          <button className="btn-next" onClick={() => navigate('/medio-tv')}>
            Siguiente &#8594;
          </button>
          <img src={require('../assets/logo-publisearch.png')} alt="Publisearch" className="header-logo-inline" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </header>

        <div className="origen2-legend">
          {Object.keys(COLORS).map((id) => (
            <span key={id} className="legend-item">
              <span className="legend-dot" style={{ background: COLORS[id] }}></span>
              {LABELS[id]}
            </span>
          ))}
        </div>

        <div className="origen2-grid">
          {ALL_YEARS.map((y) => (
            <PieChart key={y.year} data={y.data} year={y.year} size={240} />
          ))}
        </div>

        <PageFooter />
      </div>
    </PageBackground>
  );
}

export default PageMixMedioInversion2;
